import axios from 'axios';
import * as Sentry from '@sentry/react';
import { getNetworkError } from 'common/utils';

import appSettings from '../app-settings';

const CONTACTS_URL = `${appSettings.baseUrl}/contacts`;
const TAGS_URL = `${appSettings.baseUrl}/tags`;
const DONOR_SPRING_FILENAME = 'donorspringupload_';

export const getContact = async (id, archived = false) => {
  try {
    const res = await axios.get(`${CONTACTS_URL}/${id}`, { params: { archived } });
    return res.data;
  } catch (err) {
    Sentry.captureException(err);
    const errorMessage = getNetworkError(err);
    throw new Error(errorMessage);
  }
};

export const getContactEvents = async (contactId, { cursor, limit } = {}) => {
  try {
    const params = {};
    if (cursor) params.cursor = cursor;
    if (limit) params.limit = limit;

    const res = await axios.get(`${CONTACTS_URL}/${contactId}/events`, { params });

    return { events: res.data?.events ?? [], nextCursor: res.data?.nextCursor ?? null };
  } catch (err) {
    Sentry.captureException(err);
    const errorMessage = getNetworkError(err);
    throw new Error(errorMessage);
  }
};

/**
 * Manually-logged outreach. `occurredAt` is an ISO string; the API rejects
 * anything more than a minute in the future.
 */
export const createContactActivity = async (contactId, { occurredAt, medium, notes }) => {
  try {
    const res = await axios.post(`${CONTACTS_URL}/${contactId}/activities`, {
      occurredAt,
      medium,
      notes,
    });
    return res.data;
  } catch (err) {
    Sentry.captureException(err);
    throw new Error(getNetworkError(err));
  }
};

export const updateContactActivity = async (contactId, activityId, { occurredAt, medium, notes }) => {
  try {
    const res = await axios.patch(`${CONTACTS_URL}/${contactId}/activities/${activityId}`, {
      occurredAt,
      medium,
      notes,
    });
    return res.data;
  } catch (err) {
    Sentry.captureException(err);
    throw new Error(getNetworkError(err));
  }
};

export const deleteContactActivity = async (contactId, activityId) => {
  try {
    await axios.delete(`${CONTACTS_URL}/${contactId}/activities/${activityId}`);
  } catch (err) {
    Sentry.captureException(err);
    throw new Error(getNetworkError(err));
  }
};

export const deleteContactTag = async (tagId, contactId) => {
  await axios.delete(`${TAGS_URL}/${tagId}/contacts`, {
    data: { contact_ids: [contactId] },
  });
};

export const archiveContacts = async (contactIds) => {
  await axios.patch(`${CONTACTS_URL}/archive`, { contactIds });
};

export const unarchiveContacts = async (contactIds) => {
  await axios.patch(`${CONTACTS_URL}/unarchive`, { contactIds });
};

export const getSubscribedContactsCount = async () =>
  axios.get(`${CONTACTS_URL}/count`, {
    params: { unsubscribed: false },
  });

export const getSubscribedDSContactsCount = async () =>
  axios.get(`${CONTACTS_URL}/count`, {
    params: { unsubscribed: false, import_filename: DONOR_SPRING_FILENAME },
  });

export const getDSContactsProspectedCount = async () =>
  axios.get(`${CONTACTS_URL}/count`, {
    params: { import_filename: DONOR_SPRING_FILENAME, opted_in: 'any' },
  });
